import { msalWorkforceConfig, msalExternalConfig, WorkforceScopes, ExternalScopes } from '@/configs/MsalConfig'
import {
    AccountInfo, AuthenticationResult, EndSessionRequest,
    RedirectRequest, SilentRequest, SsoSilentRequest,
    InteractionRequiredAuthError, NavigationClient, PublicClientApplication
} from '@azure/msal-browser'
import { ref } from 'vue'
import { CookieModule } from '@/store/modules/savedcookies';

type MaybeAccount = AccountInfo | null

const msalWorkforceInstance = new PublicClientApplication(msalWorkforceConfig);
const msalExternalInstance = new PublicClientApplication(msalExternalConfig);
const AUTH_TYPE_KEY = "authType"; // "external" | "sso"

export const msalAuth = {
    workforceIsInitialized: ref(false),
    externalIsInitialized: ref(false),

    workforceAccount: ref<MaybeAccount>(null),
    externalAccount: ref<MaybeAccount>(null),

    useInternal: ref(false),

    async initializeWorkforce(client?: NavigationClient): Promise<MaybeAccount> {
        const msalInstance = msalWorkforceInstance

        await msalInstance.initialize()

        await msalInstance.handleRedirectPromise()
            .then((response) => {
                if (response !== null) {
                    this.workforceAccount.value = response.account
                    msalInstance.setActiveAccount(response.account)
                }
            })
            .catch((error) => {
                this.clearStorage()
                console.error(error);
                if (error instanceof InteractionRequiredAuthError) {
                    this.signInRedirect('steelking.onmicrosoft.com');
                }
            });

        if (client) {
            msalInstance.setNavigationClient(client)
        }

        const account = this.selectAccount();

        if (account != null) {
            this.workforceAccount.value = account
            msalInstance.setActiveAccount(account)
        }

        this.workforceIsInitialized.value = true
        return msalInstance.getActiveAccount()
    },

    async initializeExternal(client?: NavigationClient): Promise<MaybeAccount> {
        const msalInstance = msalExternalInstance

        await msalInstance.initialize()

        await msalInstance.handleRedirectPromise()
            .then((response) => {
                if (response !== null) {
                    this.externalAccount.value = response.account
                    msalInstance.setActiveAccount(response.account)
                    //AzureIdentificationModule.setToken(response.accessToken)
                }
            })
            .catch((error) => {
                this.clearStorage()
                console.error(error);
                if (error instanceof InteractionRequiredAuthError) {
                    this.signInRedirect(null);
                }
            });

        if (client) {
            msalInstance.setNavigationClient(client)
        }

        const account = this.selectAccount();

        if (account != null) {
            this.externalAccount.value = account
            msalInstance.setActiveAccount(account)
        }

        this.externalIsInitialized.value = true
        return msalInstance.getActiveAccount()
    },

    selectAccount(): MaybeAccount {
        const authType = this.checkAndGetCookie()
        const msalInstance = authType === "sso" ? msalWorkforceInstance : msalExternalInstance

        const currentAccounts = msalInstance.getAllAccounts();

        if (!currentAccounts) {
            console.warn('No accounts detected.')
        } else if (currentAccounts.length > 1) {
            console.warn("Multiple accounts detected.");
            return currentAccounts[0]
        } else if (currentAccounts.length === 1) {
            console.log(currentAccounts[0].username);
            return currentAccounts[0]
        }
        return null
    },

    async signInRedirect(domain: string | null): Promise<void> {
        const loginRequest: RedirectRequest = {
            scopes: ["openid", "profile", "email"]
        }

        if (domain != null) {
            loginRequest.domainHint = domain //'steelking.onmicrosoft.com'
            this.setCookie("sso")
            this.useInternal.value = true
        } else {
            this.setCookie("external")
            this.useInternal.value = false
        }

        const authType = this.checkAndGetCookie()
        console.log(authType)
        const msalInstance = authType === "sso" ? msalWorkforceInstance : msalExternalInstance

        await msalInstance.loginRedirect(loginRequest);
    },

    //async signInPopup(): Promise<MaybeAccount> {
    //    try {
    //        const loginRequest: PopupRequest = {
    //            scopes: ["openid", "profile", "email"]
    //        }

    //        const loginResponse: AuthenticationResult = await msalInstance.loginPopup(loginRequest);
    //        if (loginRequest) {
    //            return loginResponse.account
    //        }
    //        return null
    //    } catch (error) {
    //        console.log(error)
    //        return null
    //    }
    //},

    async silentSignIn(domain: string | null): Promise<MaybeAccount> {
        try {
            const loginRequest: SsoSilentRequest = {
                scopes: ["openid", "profile", "email"]
            }
            if (domain != null) {
                loginRequest.domainHint = domain //'steelking.onmicrosoft.com'
                this.setCookie("sso")
                this.useInternal.value = true
            } else {
                this.setCookie("external")
                this.useInternal.value = false
            }

            const authType = this.checkAndGetCookie()
            const msalInstance = authType === "sso" ? msalWorkforceInstance : msalExternalInstance

            const loginResponse = await msalInstance.ssoSilent(loginRequest)

            if (loginResponse) {
                const authType = this.checkAndGetCookie()
                if (authType === 'sso') {
                    this.workforceAccount.value = loginResponse.account
                } else {
                    this.externalAccount.value = loginResponse.account
                }
                msalInstance.setActiveAccount(loginResponse.account)
                return loginResponse.account
            }
            return null
        } catch (error) {

            console.log(error)
            return null
        }
    },

    async token(forceRefresh: boolean): Promise<string | null> {
        const authType = this.checkAndGetCookie()
        const msalInstance = authType === "sso" ? msalWorkforceInstance : msalExternalInstance

        const activeAccount = msalInstance.getActiveAccount()
        const tokenRequest: SilentRequest = {
            account: activeAccount ?? undefined,
            scopes: [],
            forceRefresh: forceRefresh
        }

        if (authType === "sso") {
            tokenRequest.scopes = [...WorkforceScopes]
        } else {
            tokenRequest.scopes = [...ExternalScopes]
        }

        return msalInstance.acquireTokenSilent(tokenRequest)
            .then((result: AuthenticationResult) => {
                return result.accessToken
            })
            .catch(async (error: unknown) => {
                console.log(error)
                if (error instanceof InteractionRequiredAuthError) {
                    msalInstance.acquireTokenRedirect(tokenRequest)
                }
                return null
            })
    },

    async signOut(): Promise<void> {
        const authType = this.checkAndGetCookie()
        const msalInstance = authType === "sso" ? msalWorkforceInstance : msalExternalInstance

        const logoutRequest: EndSessionRequest = {
            postLogoutRedirectUri: '/',
            account: this.selectAccount()
        };
        this.clearStorage()
        await msalInstance.logoutRedirect(logoutRequest);
    },

    checkAndGetCookie(): string {
        const name = AUTH_TYPE_KEY + '=';
        const decodedCookie = decodeURIComponent(document.cookie);
        const ca = decodedCookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            c = c.trimStart()
            while (c.charAt(0) == ' ') {
                c = c.substring(1);
            }
            if (c.indexOf(name) == 0) {
                return c.substring(name.length, c.length);
            }
        }
        return ''
    },

    setCookie(msalAuthority: string) {
        const d = new Date()
        d.setFullYear(d.getFullYear() + 1)
        const timeString = `expires=${d.toUTCString()};`
        document.cookie = `${AUTH_TYPE_KEY}=${msalAuthority};domain=.steelking.com;${timeString}path=/;`
        if (window.location.origin.indexOf('localhost') > -1) {
            document.cookie = `${AUTH_TYPE_KEY}=${msalAuthority};${timeString}path=/;`
        }
    },

    clearStorage(): void {
        sessionStorage.clear()
        if (this.checkAndGetCookie() != '') {
            document.cookie = `${AUTH_TYPE_KEY}=;domain=.steelking.com;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/`
        }
    },
}